import React, { useEffect, useState, useMemo } from "react";
import { Form, Col, Row, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { mapUserRoles } from "../../../functions/useRoleMapping";
import useHandleBlur from "../../../functions/useHandleBlur";
import { useFormState } from "../../../functions/useFormState"; // Reusable form hook
import { selectApiUrls } from "../../../redux/slices/apiUrlSlice";
import { selectTelemetry, selectTelemetryAccessories, selectFieldDescriptions } from "../../../redux/selectors";
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../redux/slices/userSlice";
import { fetchJobDetails } from "../../../redux/slices/fetchJobDetailsSlice"; // Adjust the path as necessary
import { clearSectionJobDetails } from "../../../redux/slices/jobDetailsSlice"; // Adjust the path as necessary
import {
  postData,
  deleteData,
} from "../../../redux/slices/apiCallSlice"; // Adjust the path as necessary
import { selectApiStatus } from "../../../redux/slices/apiStatusSlice"; // Import the apiStatus selector
import useFieldClass from "../../../functions/useFieldClass"; // Import the useFieldClass hook
import { postChangeLog } from "../../../redux/slices/postChangeLogSlice"; // Import postChangeLog

const TelemetryAccessory = (zone) => {
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUserDetails); // User details
  const userRoles = useSelector(selectUserRoles); // User details
  const currentUser = userDetails?.name;
  const sectionName = "telemetryAccessories"; // Name of the component

  const apiUrls = useSelector(selectApiUrls); // Fetch API URLs from Redux
  const apiUrl = apiUrls[sectionName];
  const tableKey = sectionName;

  const storedJobDetails = useSelector(selectTelemetryAccessories);
  const telemetryParent = useSelector(selectTelemetry);

  const activetab = useSelector(
    (state) => state.activeMechanicalTab.selectedZone
  );

  const apiStatus = useSelector(selectApiStatus); // Fetch API status from Redux
  const loading = apiStatus[sectionName] !== "success"; // Determine loading state based on API status

  const { options } = useSelector((state) => state.dropdown); // Fetch dropdown values from redux

  const fieldDescriptions = useSelector(selectFieldDescriptions);

  const getFieldTitle = (fieldName) => {
    return (
      fieldDescriptions.find((desc) => desc.ColumnName === fieldName)
        ?.Description || ""
    );
  };

  const renderTooltip = (fieldName) => (
    <Tooltip id={`tooltip-${fieldName}`}>
      {getFieldTitle(fieldName)
        .split("\n")
        .map((line, index) => (
          <span key={index}>
            {line}
            <br />
          </span>
        ))}
    </Tooltip>
  );

    /****************Add dropdown options here*************************/

  const typeOptions =
    options
      ?.filter((option) => option.DropdownType === "Telemetry Accessory Type")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];

  /******************************************************************/

  const parentDetails = useMemo(() => {
    return telemetryParent.find((parent) => parent.Zone === activetab) || {};
  }, [telemetryParent, activetab]);
  
  const jobDetails = useMemo(() => {
    const detail = storedJobDetails.find((detail) => detail.Zone === activetab);
    return detail || storedJobDetails[0] || {};
  }, [storedJobDetails, activetab]);

  const { formData, setFormData } = useFormState({});

  useEffect(() => {
    const filteredAccessories = storedJobDetails.filter(
      (accessory) =>
        accessory.Zone === activetab && accessory.ProjectID === jobDetails.ProjectID
    );

    const initialFormData = filteredAccessories.reduce((acc, accessory) => {
      acc[accessory.ID] = accessory;
      return acc;
    }, {});

    setFormData(initialFormData);
  }, [storedJobDetails, activetab, jobDetails, setFormData]);

  const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
  });

  const [error, setError] = useState(null);

  useEffect(() => {
    setRoles((prevRoles) => ({ ...prevRoles, ...mapUserRoles(userRoles) }));
  }, [userRoles]);


  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
    const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Fetch read-only state from Redux
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly; // Update isDisabled condition

  const { handleFocus, handleBlur } = useHandleBlur(formData);

  const handleChange = (e, accessoryId) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [accessoryId]: {
        ...prevData[accessoryId],
        [name]: value,
      },
    }));
  };

  const handleAddAccessory = async () => {
    const newAccessory = {
      projectID: parentDetails.ProjectID,
      telemetryParentID: parentDetails.ID,
      zone: activetab,
      saleQuoteID: jobDetails.SaleQuoteID,
      type: jobDetails.Type || "",
      quantity: jobDetails.Quantity || "",
    };
    
    if (
      !newAccessory.projectID ||
      !newAccessory.telemetryParentID ||
      !newAccessory.zone
    ) {
      const missingFields = [];
      if (!newAccessory.projectID) missingFields.push("Project ID");
      if (!newAccessory.telemetryParentID) missingFields.push("Telemetry Parent ID");
      if (!newAccessory.zone) missingFields.push("Zone");

      setError(
        `The following fields are missing: ${missingFields.join(", ")}. All required fields must be filled out before adding a telemetry accessory.`
      );


      return;
    }

    try {

      await dispatch(postData({ url: apiUrl, data: newAccessory })).unwrap();
      
      // Post to change log after successful addition
      const accessoryDescription = `Telemetry Accessory: ${newAccessory.type || "Unknown"}`;
      await dispatch(postChangeLog({
        projectID: parentDetails.ProjectID,
        currentUser: currentUser,
        userRole: Object.keys(roles).find(role => roles[role]) || "Unknown",
        section: "Telemetry Accessories",
        field: accessoryDescription,
        prevValue: "",
        newValue: "ADDED",
        dateTime: new Date().toISOString()
      })).unwrap();
      
      // Fetch updated job details to ensure the new accessory is included
      await dispatch(fetchJobDetails({ jobId: parentDetails.ProjectID, apiUrls: { [sectionName]: apiUrl } })).unwrap();

      const updatedDetails = await dispatch(
        fetchJobDetails({ jobId: parentDetails.ProjectID, apiUrls: { [sectionName]: apiUrl } })
      ).unwrap();

      setFormData((prevData) => {
        const updatedFormData = { ...prevData };
        if (updatedDetails[sectionName]) {
          updatedDetails[sectionName].forEach((accessory) => {
            if (
              accessory.Zone === activetab &&
              accessory.ProjectID === telemetryParent.ProjectID
            ) {
              updatedFormData[accessory.ID] = accessory;
            }
          });
        }
        return updatedFormData;
      });

      // Force re-render by updating the state
      setFormData((prevData) => ({ ...prevData }));

    } catch (error) {
      console.error("Failed to add telemetry accessory:", error);
      setError(
        error.message || "An error occurred while adding the telemetry accessory."
      );
    }
  };

  const handleDeleteAccessory = async (accessoryId) => {
    if (!accessoryId) {
      setError("Invalid telemetry accessory ID.");
      return;
    }

    try {
      // Find the accessory to delete for change log
      const accessoryToDelete = formData[accessoryId];
      
      await dispatch(
        deleteData({ url: apiUrl, deleteId: { ID: accessoryId } })
      ).unwrap();

      // Post to change log after successful deletion
      if (accessoryToDelete) {
        const accessoryDescription = `Telemetry Accessory: ${accessoryToDelete.Type || "Unknown"}`;
        await dispatch(postChangeLog({
          projectID: jobDetails.ProjectID,
          currentUser: currentUser,
          userRole: Object.keys(roles).find(role => roles[role]) || "Unknown",
          section: "Telemetry Accessories",
          field: accessoryDescription,
          prevValue: "",
          newValue: "DELETED",
          dateTime: new Date().toISOString()
        })).unwrap();
      }

      dispatch(clearSectionJobDetails(sectionName));

      // Fetch updated job details to ensure the deleted accessory is removed from the Redux store
      const updatedDetails = await dispatch(
        fetchJobDetails({ jobId: jobDetails.ProjectID, apiUrls: { [sectionName]: apiUrl } })
      ).unwrap();

      setFormData((prevData) => {
        const updatedFormData = { ...prevData };
        delete updatedFormData[accessoryId]; // Remove the deleted accessory from formData
        if (updatedDetails[sectionName]) {
          updatedDetails[sectionName].forEach((accessory) => {
            if (
              accessory.Zone === activetab &&
              accessory.ProjectID === jobDetails.ProjectID
            ) {
              updatedFormData[accessory.ID] = accessory;
            }
          });
        }
        return updatedFormData;
      });

      // Force re-render by updating the state
      setFormData((prevData) => ({ ...prevData }));

      
    } catch (error) {
      console.error("Failed to delete telemetry accessory:", error);
      setError(
        error.message || "An error occurred while deleting the telemetry accessory."
      );
    }
  };

  const isValueInDropdown = (value, dropdownOptions) => {
    const exists = dropdownOptions.some(
      (option) => option.DisplayText === value || option.displayName === value
    );
    return exists;
  };

  const fieldClasses = useFieldClass(formData, activetab); // Use useFieldClass hook

  // Helper function to determine the highlight class for a field
  const getFieldHighlightClass = (fieldName, accessoryId) => {
    if (fieldClasses?.[accessoryId]?.[fieldName]) {
      return fieldClasses[accessoryId][fieldName]; // Use fieldClasses for dynamic highlighting
    }
    return ""; // Default class
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="error">{error}</div>; // Show error message if fetch fails
  }

  return (
    <div className="telemetry-accessories-responsive">
      <h1 className="subsection-header">Telemetry Accessories</h1>
      {Object.keys(formData).map((accessoryId) => {
        const accessory = formData[accessoryId];

        return (
          <Row key={accessory.ID} className="mb-3 telemetry-accessory-record">
            {/* Telemetry Type */}
            <Col className="telemetry-accessory-field">
              <Form.Group as={Row} controlId={`type-${accessory.ID}`}>
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("Type")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Type</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="Type"
                    value={accessory.Type || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(
                        e,
                        "Type",
                        typeOptions,
                        {
                          ID: accessory.ID,
                          url: apiUrl,
                          field: "Type",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={getFieldHighlightClass("Type", accessory.ID)} // Apply dynamic class
                    onChange={(e) => handleChange(e, accessory.ID)}
                  >
                    <option value="">Select Type</option>
                    {typeOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                    {!isValueInDropdown(
                      accessory.Type,
                      typeOptions
                    ) &&
                      accessory.Type && (
                        <option
                          value={accessory.Type}
                          style={{ color: "red" }}
                        >
                          *{accessory.Type}
                        </option>
                      )}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>

            {/* Quantity */}
            <Col className="telemetry-accessory-field">
              <Form.Group as={Row} controlId={`quantity-${accessory.ID}`}>
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("Quantity")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Quantity</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Control
                    type="text"
                    name="Quantity"
                    value={accessory.Quantity || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(
                        e,
                        "Quantity",
                        [],
                        {
                          ID: accessory.ID,
                          url: apiUrl,
                          field: "Quantity",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={getFieldHighlightClass("Quantity", accessory.ID)} // Apply dynamic class
                    onChange={(e) => handleChange(e, accessory.ID)}
                    placeholder="Enter Quantity"
                  />
                </Col>
              </Form.Group>
            </Col>

            {/* Delete Button */}
            <Col sm={1} className="telemetry-accessory-actions">
              <Button
                variant="link"
                onClick={() => handleDeleteAccessory(accessory.ID)}
                disabled={isDisabled || (!roles.Admin && !roles.Apps)}
              >
                <i className="bi bi-trash"></i>
              </Button>
            </Col>
          </Row>
        );
      })}
      <Button
        className="telemetry-accessory-add-button"
        variant="primary"
        onClick={handleAddAccessory}
        disabled={isDisabled || (!roles.Admin && !roles.Apps)}
      >
        Add Telemetry Accessory
      </Button>
    </div>
  );
};

export default TelemetryAccessory;
